import React from 'react';
import { FiUsers,  FiCheckCircle, FiClock, FiBarChart2, FiMail, FiCalendar, FiTarget } from 'react-icons/fi';

const CoachingCentre = () => {
  const features = [
    {
      icon: <FiUsers className="w-6 h-6" />,
      title: 'Student Enrollment and Batch Management',
      description: 'Coaching centres operate in batches—different courses, different levels, different timings. The system must manage enrollment across batches, track student progress, and handle batch transitions smoothly. No more manual spreadsheets for batch management.',
      color: 'bg-indigo-50'
    },
    {
      icon: <FiTarget className="w-6 h-6" />,
      title: 'Competitive Exam Preparation Tracking',
      description: 'Coaching centres prepare students for competitive exams—JEE, NEET, CLAT, CAT, or government exams. The system should track exam-specific preparation, mock test performance, and progress toward exam readiness.',
      color: 'bg-emerald-50'
    },
    {
      icon: <FiBarChart2 className="w-6 h-6" />,
      title: 'Mock Test and Performance Analytics',
      description: 'Mock tests are the backbone of coaching. The system should manage test schedules, grade tests quickly, and provide detailed performance analytics. Students and parents need to see progress clearly.',
      color: 'bg-amber-50'
    },
    {
      icon: <FiClock className="w-6 h-6" />,
      title: 'Attendance and Fee Management',
      description: 'Coaching centres need rigorous attendance tracking—especially for competitive exam students. Fee management must handle different fee structures, discounts, and payment schedules. Late fee alerts and reminders are essential.',
      color: 'bg-rose-50'
    },
    {
      icon: <FiMail className="w-6 h-6" />,
      title: 'Parent Communication and Progress Reports',
      description: 'Parents of coaching students are deeply involved. They want regular updates on attendance, test performance, and progress. The system should automate parent communication and generate comprehensive progress reports.',
      color: 'bg-purple-50'
    },
    {
      icon: <FiCalendar className="w-6 h-6" />,
      title: 'Faculty and Class Scheduling',
      description: 'Coaching centres have complex scheduling needs—different batches, different subjects, different faculty. The system must handle scheduling conflicts, track faculty availability, and manage substitute arrangements.',
      color: 'bg-blue-50'
    }
  ];

  const benefits = [
    {
      title: 'Track Student Progress Effectively',
      description: 'Monitor individual student performance across mock tests and identify areas needing improvement quickly.'
    },
    {
      title: 'Automate Batch Management',
      description: 'Manage multiple batches, courses, and levels without manual spreadsheets or confusion.'
    },
    {
      title: 'Improve Parent Communication',
      description: 'Keep parents informed with automated progress reports, attendance alerts, and test results.'
    },
    {
      title: 'Scale Your Coaching Centre',
      description: 'Open new branches or expand existing ones without administrative chaos. Systems that scale with you.'
    },
    {
      title: 'Increase Operational Efficiency',
      description: 'Automate fee collection, attendance tracking, and test grading. Focus on teaching instead of admin.'
    },
    {
      title: 'Build Your Brand Reputation',
      description: 'Consistent quality across batches and branches builds parent trust and student loyalty.'
    }
  ];

  return (
    <>
      {/* Introduction */}
      <p className="text-lg text-slate-600 leading-relaxed mb-6">
        Coaching centres are a vital part of India's education ecosystem. From preparing students for competitive exams to providing specialized training, these institutions shape the careers of millions of students every year. The stakes are high—students' futures depend on their success.
      </p>
      
      <p className="text-lg text-slate-600 leading-relaxed mb-6">
        But running a coaching centre is not easy. <strong className="text-slate-900">Managing batches, tracking student progress, conducting mock tests, communicating with parents, handling fees—the administrative burden is immense.</strong> Without the right systems, quality suffers, and students pay the price.
      </p>

      <p className="text-lg text-slate-600 leading-relaxed mb-8">
        Here's how the right coaching centre management software helps you scale your institute while maintaining quality.
      </p>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <div className="bg-white rounded-xl p-6 text-center border">
          <div className="text-3xl font-extrabold text-indigo-600">50,000+</div>
          <div className="text-sm text-slate-500">Coaching centres across India</div>
        </div>
        <div className="bg-white rounded-xl p-6 text-center border">
          <div className="text-3xl font-extrabold text-amber-600">2 Crore+</div>
          <div className="text-sm text-slate-500">Students enrolled in coaching</div>
        </div>
        <div className="bg-white rounded-xl p-6 text-center border">
          <div className="text-3xl font-extrabold text-rose-600">50%</div>
          <div className="text-sm text-slate-500">Faster growth with the right ERP</div>
        </div>
      </div>

      <h2 className="text-2xl font-bold text-slate-900 mb-6">Essential Features for Coaching Centres</h2>

      {/* Features List */}
      {features.map((item, i) => (
        <div key={i} className={`${item.color} rounded-2xl p-6 mb-6 border`}>
          <div className="flex items-start gap-4">
            <div className="bg-white rounded-xl p-3 shadow-sm flex-shrink-0 mt-1">
              {item.icon}
            </div>
            <div className="flex-1">
              <h3 className="text-xl font-bold text-slate-900 mb-2">{item.title}</h3>
              <p className="text-slate-600 leading-relaxed text-sm">{item.description}</p>
            </div>
          </div>
        </div>
      ))}

      {/* Benefits Section */}
      <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-6 sm:p-8">
        <h3 className="text-xl font-bold text-slate-900 mb-4">What the Right ERP Delivers for Coaching Centres</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {benefits.map((item, i) => (
            <div key={i} className="bg-indigo-50 rounded-xl p-4 border border-indigo-200">
              <div className="flex items-start gap-2">
                <FiCheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-bold text-slate-900 text-sm">{item.title}</h4>
                  <p className="text-xs text-slate-600">{item.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Comparison Section */}
      <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-6 sm:p-8">
        <h3 className="text-xl font-bold text-slate-900 mb-4">Manual vs. Automated Coaching Management</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="bg-red-50 rounded-xl p-4 border border-red-200">
            <div className="text-sm font-semibold text-red-700 mb-2">Manual Management</div>
            <ul className="text-sm text-slate-600 space-y-2">
              <li className="flex items-start gap-2">• <span>Spreadsheets for everything</span></li>
              <li className="flex items-start gap-2">• <span>Manual attendance tracking</span></li>
              <li className="flex items-start gap-2">• <span>Handwritten progress reports</span></li>
              <li className="flex items-start gap-2">• <span>Parent communication is ad-hoc</span></li>
              <li className="flex items-start gap-2">• <span>Hard to scale or open new centres</span></li>
            </ul>
          </div>
          <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-200">
            <div className="text-sm font-semibold text-emerald-700 mb-2">Automated ERP Management</div>
            <ul className="text-sm text-slate-600 space-y-2">
              <li className="flex items-start gap-2">• <span>All data in one integrated platform</span></li>
              <li className="flex items-start gap-2">• <span>Automated attendance and tracking</span></li>
              <li className="flex items-start gap-2">• <span>Professional progress reports generated instantly</span></li>
              <li className="flex items-start gap-2">• <span>Automated parent communication</span></li>
              <li className="flex items-start gap-2">• <span>Scale effortlessly to new branches</span></li>
            </ul>
          </div>
        </div>
      </div>

      {/* Why Truyon Section */}
      <div className="mt-8 bg-indigo-50 rounded-2xl p-6 border border-indigo-200">
        <h3 className="text-lg font-bold text-indigo-900 mb-3">Why Truyon is the Best Choice for Coaching Centres</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">🎯</div>
            <h4 className="font-bold text-slate-900 text-sm">Exam Prep Tracking</h4>
            <p className="text-xs text-slate-600">Track JEE, NEET, CAT, or government exam preparation</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">📊</div>
            <h4 className="font-bold text-slate-900 text-sm">Mock Test Analytics</h4>
            <p className="text-xs text-slate-600">Detailed performance analysis and improvement tracking</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">📚</div>
            <h4 className="font-bold text-slate-900 text-sm">Batch Management</h4>
            <p className="text-xs text-slate-600">Handle multiple batches, courses, and levels</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">💬</div>
            <h4 className="font-bold text-slate-900 text-sm">Parent Communication</h4>
            <p className="text-xs text-slate-600">Automated progress reports and updates</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">💰</div>
            <h4 className="font-bold text-slate-900 text-sm">Fee Management</h4>
            <p className="text-xs text-slate-600">Automated fee tracking and payment reminders</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">⚡</div>
            <h4 className="font-bold text-slate-900 text-sm">Scalable</h4>
            <p className="text-xs text-slate-600">Open new centres and scale without administrative chaos</p>
          </div>
        </div>
      </div>

      {/* The Growth Journey */}
      <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-6">
        <h3 className="text-lg font-bold text-slate-900 mb-3">The Scaling Journey for Coaching Centres</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-indigo-50 rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">📋</div>
            <h4 className="font-bold text-slate-900 text-sm">Foundation</h4>
            <p className="text-xs text-slate-600">Get systems right before scaling</p>
          </div>
          <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-200 text-center">
            <div className="text-2xl mb-1">🚀</div>
            <h4 className="font-bold text-slate-900 text-sm">Growth</h4>
            <p className="text-xs text-slate-600">Open new branches with standardized processes</p>
          </div>
          <div className="bg-amber-50 rounded-xl p-4 border border-amber-200 text-center">
            <div className="text-2xl mb-1">🏆</div>
            <h4 className="font-bold text-slate-900 text-sm">Excellence</h4>
            <p className="text-xs text-slate-600">Maintain quality while scaling</p>
          </div>
        </div>
      </div>

      {/* The Human Impact */}
      <div className="mt-8 bg-amber-50 rounded-2xl p-6 border border-amber-200">
        <h3 className="text-lg font-bold text-amber-900 mb-3">Beyond Administration: The Real Impact</h3>
        <p className="text-amber-800 leading-relaxed mb-3">
          Behind every coaching centre are students dreaming of success. Parents investing their hopes and savings. Teachers dedicated to making a difference. The right systems support all of them.
        </p>
        <p className="text-amber-800 leading-relaxed mb-3">
          When administrative work is automated, teachers can focus on teaching. When progress is tracked effectively, students know where they stand. When parents are informed regularly, anxiety decreases. Everyone wins.
        </p>
        <p className="text-amber-800 leading-relaxed">
          <strong>Your coaching centre's success depends on student success. Student success depends on quality teaching. Quality teaching depends on the right systems.</strong>
        </p>
      </div>

      {/* Conclusion */}
      <div className="mt-8 bg-indigo-50 rounded-2xl p-6 border border-indigo-200">
        <h3 className="text-lg font-bold text-indigo-900 mb-2">Scale Your Coaching Centre with Truyon</h3>
        <p className="text-indigo-800 leading-relaxed">
          Coaching centres are transforming the lives of students across India. The right management system helps you focus on what matters most—quality teaching and student success.
        </p>
        <p className="text-indigo-800 leading-relaxed mt-3">
          Truyon provides coaching centre management software that handles batch management, mock test analytics, parent communication, fee tracking, and more. All in one platform.
        </p>
        <p className="text-indigo-800 leading-relaxed mt-3">
          <strong>Truyon for coaching centres.</strong> Built for growth. Built for quality. Ready to help you scale.
        </p>
      </div>
    </>
  );
};

export default CoachingCentre;